import {
  getUrlWithVariant,
  Product as ShopifyProduct,
  ProductVariant as ShopifyProductVariant,
} from '@shopify/theme-product-form'
import { bind } from 'decko'
import { LiveElement, LiveNodeList } from 'live-node-list'
import { formatMoney, retrieveMetaobject } from '../helpers'
import { ProductVariantChangeEvent } from './product-events'
import { productMetafieldQuery, variantMetafieldQuery } from '../queries'
import { fetchFromApi } from './api'

/**
 * @type {Object<{[key: string]: string}>}
 */
const selectors = {
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  price: '[data-product-price]',
  priceWrapper: '[data-price-wrapper]',
  quantityInput: '[data-quantity-input]',
  submitButton: '[data-submit-button]:not(.rfq-btn)',
  submitButtonText: ':not(.rfq-btn) > [data-submit-button-text]',
  buyButton: '[data-buy-button]',
  buyButtonText: '[data-buy-button-text]',
  variantSelector: '[data-product-select]',
  minimumOrderQuantity: '[data-moq]',
  minimumOrderQuantityValue: '[data-moq-value]',
}

// A list of product metafields to fetch
// e.g. ['namespace.key']
export const productMetafields = ['product.short_description']

// A list of variant metafields to fetch
export const variantMetafields = [
  'product.minimum_order_quantity',
  'product.quantity_increment',
]

export default class ProductForm {
  /**
   * @type {HTMLElement}
   */
  content = null

  /**
   * @type {HTMLFormElement}
   */
  element = null

  /**
   * @type {ShopifyProduct}
   */
  product = null

  /**
   * @type {ShopifyProductVariant}
   */
  variant = null

  /**
   * @type {Object<string, LiveNodeList<HTMLElement>>}
   */
  elements = {}

  /**
   * @type {Array<{name: string, value: string}>}
   */
  options = []

  constructor(element, product, variant) {
    this.content = element.closest('.product-content')
    this.element = element
    this.product = product
    this.variant = variant

    this.options = this.element.shopifyProductForm.options()

    this.elements = {
      comparePrice: new LiveNodeList(selectors.comparePrice, this.content),
      comparePriceText: new LiveNodeList(
        selectors.comparePriceText,
        this.content,
      ),
      priceWrapper: new LiveNodeList(selectors.priceWrapper, this.content),
      price: new LiveNodeList(selectors.price, this.content),
      quantityInputs: new LiveNodeList(selectors.quantityInput, this.element),
      submitButton: new LiveNodeList(selectors.submitButton, this.element),
      submitButtonText: new LiveNodeList(
        selectors.submitButtonText,
        this.element,
      ),
      buyButton: new LiveNodeList(selectors.buyButton, this.element),
      buyButtonText: new LiveNodeList(selectors.buyButtonText, this.element),
      variantSelector: new LiveNodeList(
        selectors.variantSelector,
        this.element,
      ),
      minimumOrderQuantity: new LiveNodeList(
        selectors.minimumOrderQuantity,
        this.element,
      ),
      minimumOrderQuantityValue: new LiveNodeList(
        selectors.minimumOrderQuantityValue,
        this.element,
      ),
    }

    this.registerListeners()

    this.elements.variantSelector.items[0]?.dispatchEvent(new Event('change'))

    this.populateProductMetafields(productMetafields)
    if (this.variant) {
      this.populateVariantMetafields(variantMetafields)
      this.elements.variantSelector.item?.dispatchEvent(new Event('change'))
    }
  }

  registerListeners() {
    this.element.addEventListener('product:option:change', async event => {
      this.variant = event.variant
      this.options = event.options
      this.quantity = event.quantity

      await this.populateVariantMetafields(variantMetafields)

      this.updateSelectedVariant()
      this.updateBrowserHistory()
      this.updatePrice()
      this.updateAvailability()
      this.updateSwatchValues()
    })

    this.element.addEventListener('product:quantity:change', async event => {
      this.quantity = event.quantity

      this.updatePrice()
      this.updateAvailability()
    })

    this.element.addEventListener('product:property:change', async event => {
      this.properties = event.properties
    })
  }

  @bind
  async populateProductMetafields(keys) {
    const metafieldQuery = productMetafieldQuery(
      this.product.id.toString(),
      keys,
    )
    const metafieldData = await fetchFromApi(metafieldQuery)

    this.product.metafields = {}
    for (const fullKey of keys) {
      const [namespace, key] = fullKey.split('.')

      if (!this.product.metafields[namespace]) {
        this.product.metafields[namespace] = {}
      }

      let value =
        metafieldData.data.products?.edges?.[0]?.node[`${namespace}___${key}`]
          ?.value

      if (value?.startsWith('gid://shopify/Metaobject/')) {
        value = await retrieveMetaobject(value)
      }

      this.product.metafields[namespace][key] = value
    }
  }

  @bind
  async populateVariantMetafields(keys) {
    const metafieldQuery = variantMetafieldQuery(
      this.product.id.toString(),
      [],
      keys,
    )
    const metafieldData = await fetchFromApi(metafieldQuery)

    this.variant.metafields = {}
    for (const fullKey of keys) {
      const [namespace, key] = fullKey.split('.')

      if (!this.variant.metafields[namespace]) {
        this.variant.metafields[namespace] = {}
      }

      let value =
        metafieldData.data.products?.edges?.[0]?.node.variants?.edges?.find(
          edge =>
            edge.node.id === `gid://shopify/ProductVariant/${this.variant.id}`,
        )?.node?.[`${namespace}___${key}`]?.value

      if (value?.startsWith('gid://shopify/Metaobject/')) {
        value = await retrieveMetaobject(value)
      }

      this.variant.metafields[namespace][key] = value
    }
  }

  @bind
  updateSelectedVariant() {
    this.elements.variantSelector.forEach(selector => {
      selector.value = this.variant.id
    })

    this.updateAvailableQuantity()

    this.element.dispatchEvent(
      new ProductVariantChangeEvent({
        form: this.element,
        options: this.options,
        product: this.product,
        properties: this.properties,
        quantity: this.quantity,
        variant: this.variant,
      }),
    )
  }

  @bind
  updateBrowserHistory() {
    const enableHistoryState = this.element.dataset.enableHistoryState

    if (!this.variant || enableHistoryState !== 'true') {
      return
    }

    const url = getUrlWithVariant(window.location.href, this.variant.id)
    window.history.replaceState({ path: url }, '', url)
  }

  @bind
  updatePrice() {
    const price = this.variant.price
    this.elements.price.forEach(
      element => (element.innerHTML = formatMoney(price)),
    )

    const comparePrice = this.variant.compare_at_price
    this.elements.comparePrice.forEach(element => {
      element.setAttribute('aria-hidden', (comparePrice <= price).toString())
      element.innerHTML = formatMoney(
        comparePrice > price ? comparePrice : price,
      )
    })
  }

  @bind
  updateAvailability() {
    let disabled = false
    let buttonText = window.theme.strings.addToCart
    let buyButtonText =
      this.product.variants.length > 1
        ? window.theme.strings.configure
        : window.theme.strings.buyNow

    const availableQuantity = parseInt(
      this.elements.quantityInputs.items[0]?.max || 999,
      10,
    )

    if (!this.variant) {
      disabled = true
      buttonText = window.theme.strings.unavailable
      buyButtonText = window.theme.strings.unavailable
    } else if (!this.variant.available) {
      disabled = true
      buttonText = window.theme.strings.soldOut
      buyButtonText = window.theme.strings.soldOut
    } else if (availableQuantity < this.quantity) {
      disabled = true
      buttonText = window.theme.strings.quantityUnavailable
      buyButtonText = window.theme.strings.quantityUnavailable
    }

    this.elements.submitButton.forEach(button => {
      button.disabled = disabled
    })

    this.elements.submitButtonText.forEach(button => {
      button.innerText = buttonText
    })
    this.elements.buyButtonText.forEach(button => {
      button.innerText = buyButtonText
    })
  }

  @bind
  updateSwatchValues() {
    const swatches = this.element.querySelectorAll(
      '.product-options-swatch__value',
    )
    swatches.forEach(swatch => {
      const name = swatch.dataset.option
      const value = this.options.find(option => option.name === name).value

      swatch.innerText = value
    })
  }

  @bind
  updateAvailableQuantity() {
    const min = this.variant.metafields?.product?.minimum_order_quantity || 1
    const step = this.variant.metafields?.product?.quantity_increment || 1
    const max = parseInt(
      [
        ...this.elements.variantSelector.items[0]?.querySelectorAll('option'),
      ].find(option => !!option.selected)?.dataset.quantityAvailable || 0,
    )

    this.elements.quantityInputs.forEach(input => {
      input.min = Math.max(min, 1)
      input.step = Math.max(step, 1)
      input.max = Math.max(max, 0)

      input.dispatchEvent(new Event('change'))
    })

    this.elements.minimumOrderQuantity.forEach(element => {
      const hidden = min === 1
      element.setAttribute('aria-hidden', hidden.toString())
    })

    this.elements.minimumOrderQuantityValue.forEach(element => {
      element.innerText = min
    })
  }
}
